/**
 * Atlassian/Jira Integration API
 *
 * API endpoints for managing Atlassian/Jira OAuth connections and credentials
 */

import fetchWithAuth from "@/api/secureFetch";
import type { ContextMeshRefreshNotice } from "./links";

// Jira/ADO endpoints now live in app/backend (ported from the retired agent service)
const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface AtlassianCredentialsCheckResponse {
    hasCredentials: boolean;
    cloudId?: string;
    domain?: string;
    email?: string | null;
    projectId?: string;
    projectName?: string;
}

export interface VerifyDomainRequest {
    domain: string;
}

export interface VerifyDomainResponse {
    success: boolean;
    cloudId?: string;
    siteName?: string;
    siteUrl?: string;
    error?: string;
}

export interface InitiateOAuthRequest {
    userId: number;
    domain: string;
    cloudId?: string;
}

export interface InitiateOAuthResponse {
    authUrl?: string;
    authorizationUrl?: string;
    state: string;
}

export interface OAuthCallbackParams {
    code: string;
    state: string;
}

export interface OAuthCallbackResponse {
    success: boolean;
    message?: string;
    error?: string;
    cloudId?: string;
    accessToken?: string;
}

export interface DeleteCredentialsResponse extends ContextMeshRefreshNotice {
    success: boolean;
    message: string;
}

// Atlassian Info Endpoint Types
export type AtlassianInfoStatus = "NOT_LINKED" | "NOT_ACTIVE" | "LINKED";

export interface AtlassianInfoRequest {
    userId: number;
    projectId?: number;
}

export interface AtlassianInfoDetails {
    domain: string;
    cloudId: string;
    projectId: string;
    projectName: string;
    projectKey: string;
}

export interface AtlassianInfoResponse {
    status: AtlassianInfoStatus;
    message: string;
    atlassian?: AtlassianInfoDetails; // Only present when status is "LINKED"
}

// Atlassian Sync Endpoint Types
export interface AtlassianSyncRequest {
    userId: number;
}

export interface AtlassianSyncResponse extends ContextMeshRefreshNotice {
    status: AtlassianInfoStatus;
    message?: string;
    success?: boolean; // Present when status is "LINKED"
    deleted?: number; // Number of records deleted
    imported?: number; // Number of records imported
    error?: string; // Present when success is false
}

/**
 * Check if user has existing Atlassian credentials
 */
export async function checkAtlassianCredentials(
    userId: number,
    token: string
): Promise<AtlassianCredentialsCheckResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/credentials/check?userId=${userId}`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to check credentials: ${response.status}`);
    }

    return response.json();
}

/**
 * Verify Atlassian domain and get cloud ID
 */
export async function verifyAtlassianDomain(
    domain: string,
    token: string
): Promise<VerifyDomainResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/verify-domain`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ domain })
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to verify domain: ${response.status}`);
    }

    return response.json();
}

/**
 * Initiate OAuth flow for Atlassian
 */
export async function initiateAtlassianOAuth(
    request: InitiateOAuthRequest,
    token: string
): Promise<InitiateOAuthResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/oauth/authorize`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }

    return response.json();
}

/**
 * Handle OAuth callback (typically called by backend redirect, but exposed for frontend if needed)
 */
export async function handleAtlassianOAuthCallback(
    params: OAuthCallbackParams,
    token: string
): Promise<OAuthCallbackResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/oauth/callback?code=${params.code}&state=${params.state}`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`OAuth callback failed: ${response.status}`);
    }

    return response.json();
}

/**
 * Get Atlassian integration info/status for a user
 *
 * Returns one of three statuses:
 * - NOT_LINKED: No credential exists for the user
 * - NOT_ACTIVE: Credential exists but is deactivated
 * - LINKED: Active credential with full atlassian config details
 */
export async function getAtlassianInfo(
    request: AtlassianInfoRequest,
    token: string
): Promise<AtlassianInfoResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/info`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to get Atlassian info: ${response.status}`);
    }

    return response.json();
}

/**
 * Sync Jira data for a user
 *
 * Fetches all issues from the user's linked Jira project and syncs them to the database.
 * Uses the project stored in the user's credential config.
 *
 * Response statuses:
 * - NOT_LINKED: No credential exists for the user
 * - NOT_ACTIVE: Credential exists but is deactivated
 * - LINKED with success=true: Sync completed successfully
 * - LINKED with success=false: Jira API failed (existing data preserved)
 */
export async function syncAtlassianData(
    request: AtlassianSyncRequest,
    token: string
): Promise<AtlassianSyncResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/sync`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to sync Atlassian data: ${response.status}`);
    }

    return response.json();
}

export interface JiraProjectRecord {
    id: string;
    key: string;
    name: string;
}

/**
 * List Jira projects the authorized account can access for a cloud ID
 */
export async function listJiraProjects(
    cloudId: string,
    token: string
): Promise<{ projects: JiraProjectRecord[] }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/atlassian/projects`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cloudId })
    });
    if (!response.ok) {
        throw new Error(`Failed to list Jira projects: ${response.status}`);
    }
    return response.json();
}

/**
 * Persist the selected Jira project on the user's credential
 */
export async function selectJiraProject(
    req: { cloudId: string; projectId: string; projectName: string },
    token: string
): Promise<{ success: boolean; message?: string }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/atlassian/project/select`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req)
    });
    if (!response.ok) {
        throw new Error(`Failed to select Jira project: ${response.status}`);
    }
    return response.json();
}

export interface ImportProgress {
    stage: string;
    current: number;
    total: number;
    message?: string;
}

export interface ImportResult extends ContextMeshRefreshNotice {
    imported: number;
    failed: number;
    process_job_id?: string;
}

/**
 * Import the linked project's tickets, streaming progress via SSE.
 * Backend events: progress {stage, current, total, message},
 * complete {imported, failed, ...}, error {message}.
 */
export async function importTicketsStream(
    req: { cloudId: string; projectId: string; projectName: string; projectKey: string },
    token: string,
    onProgress: (p: ImportProgress) => void,
    signal?: AbortSignal
): Promise<ImportResult> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/atlassian/import-tickets-stream`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
        signal
    });
    if (!response.ok || !response.body) {
        throw new Error(`Failed to start ticket import: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // SSE frames are `event: <type>\ndata: <json>\n\n`; chunks may split frames.
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");

            const eventMatch = frame.match(/^event: (.+)$/m);
            const dataMatch = frame.match(/^data: (.+)$/m);
            if (!eventMatch || !dataMatch) continue;

            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(dataMatch[1]);
            } catch {
                continue;
            }

            if (eventMatch[1] === "error") {
                throw new Error(typeof payload.message === "string" ? payload.message : "Import failed");
            }
            if (eventMatch[1] === "complete") {
                const refresh = payload.context_mesh_refresh;
                const result: ImportResult = {
                    imported: typeof payload.imported === "number" ? payload.imported : 0,
                    failed: typeof payload.failed === "number" ? payload.failed : 0,
                    process_job_id: typeof payload.process_job_id === "string" ? payload.process_job_id : undefined
                };
                if (refresh && typeof refresh === "object") {
                    result.context_mesh_refresh = refresh as ContextMeshRefreshNotice["context_mesh_refresh"];
                }
                if (typeof payload.warning === "string") result.warning = payload.warning;
                return result;
            }
            if (eventMatch[1] === "progress") {
                onProgress({
                    stage: String(payload.stage ?? ""),
                    current: typeof payload.current === "number" ? payload.current : 0,
                    total: typeof payload.total === "number" ? payload.total : 0,
                    message: typeof payload.message === "string" ? payload.message : undefined
                });
            }
        }
    }
    throw new Error("Import stream ended unexpectedly");
}

/**
 * Disconnect the current user's Atlassian integration.
 */
export async function deleteAtlassianCredentials(token: string): Promise<DeleteCredentialsResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/atlassian/credentials/delete`,
        token,
        {
            method: "DELETE"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to delete Atlassian credentials: ${response.status}`);
    }

    return response.json();
}
